import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  LinearProgress,
  Grid,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Visibility as PreviewIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { PatternUploadProps, PATTERN_CONSTANTS } from '../../types/pattern';
import PatternPreview from './PatternPreview';

const PatternUpload: React.FC<PatternUploadProps> = ({
  pattern,
  view,
  onUpload,
  onCancel,
  loading = false,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [previewContent, setPreviewContent] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentSvgUrl = view === 'front' ? pattern.front_svg_content : pattern.side_svg_content;

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!PATTERN_CONSTANTS.ALLOWED_FILE_TYPES.includes(file.type as any) &&
        !PATTERN_CONSTANTS.ALLOWED_FILE_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext))) {
      return 'Please select a valid SVG file';
    }

    // Check file size
    if (file.size > PATTERN_CONSTANTS.MAX_FILE_SIZE) {
      return `File size must be less than ${PATTERN_CONSTANTS.MAX_FILE_SIZE / (1024 * 1024)}MB`;
    }

    return null;
  };

  const handleFileSelect = async (file: File) => {
    const error = validateFile(file);
    if (error) {
      setValidationError(error);
      return;
    }

    setValidationError(null);
    setSelectedFile(file);

    // Read file content for preview
    try {
      const content = await file.text();
      if (content.includes('<svg')) {
        setPreviewContent(content);
      } else {
        setValidationError('Invalid SVG file content');
        setSelectedFile(null);
      }
    } catch (err) {
      setValidationError('Failed to read file content');
      setSelectedFile(null);
    }
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    
    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleUpload = () => {
    if (selectedFile) {
      onUpload(selectedFile);
    }
  };

  const handleClearSelection = () => {
    setSelectedFile(null);
    setPreviewContent(null);
    setValidationError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Current SVG Status */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Upload {view.charAt(0).toUpperCase() + view.slice(1)} View SVG
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Pattern: {pattern.description}
        </Typography>
        
        {currentSvgUrl ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Current {view} SVG is available. Uploading a new file will replace it.
            </Typography>
          </Alert>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              No {view} SVG currently available for this pattern.
            </Typography>
          </Alert>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Upload Area */}
        <Grid item xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              border: '2px dashed',
              borderColor: dragOver ? 'primary.main' : 'grey.300',
              bgcolor: dragOver ? 'primary.light' : 'grey.50',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'primary.light',
              },
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <Box sx={{ textAlign: 'center' }}>
              <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Drop SVG file here or click to browse
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Supported formats: SVG
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Maximum size: {PATTERN_CONSTANTS.MAX_FILE_SIZE / (1024 * 1024)}MB
              </Typography>
            </Box>
          </Paper>

          <input
            ref={fileInputRef}
            type="file"
            accept=".svg,image/svg+xml"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />

          {/* File Information */}
          {selectedFile && (
            <Paper sx={{ p: 2, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected File
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(selectedFile.size)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip
                    icon={<CheckIcon />}
                    label="Valid SVG"
                    color="success"
                    size="small"
                  />
                  <Tooltip title="Remove file">
                    <IconButton size="small" onClick={handleClearSelection}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Validation Error */}
          {validationError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {validationError}
            </Alert>
          )}
        </Grid>

        {/* Preview Area */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" gutterBottom>
            Preview
          </Typography>
          <Paper sx={{ p: 2, minHeight: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {previewContent ? (
              <Box sx={{ textAlign: 'center' }}>
                <div
                  dangerouslySetInnerHTML={{ __html: previewContent }}
                  style={{
                    maxWidth: '150px',
                    maxHeight: '200px',
                    margin: '0 auto',
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  New {view} view preview
                </Typography>
              </Box>
            ) : currentSvgUrl ? (
              <Box sx={{ textAlign: 'center' }}>
                <PatternPreview
                  pattern={pattern}
                  view={view}
                  size="medium"
                  showFallback
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Current {view} view
                </Typography>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
                <PreviewIcon sx={{ fontSize: 48, mb: 1 }} />
                <Typography variant="body2">
                  No preview available
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Loading Progress */}
      {loading && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" gutterBottom>
            Uploading SVG file...
          </Typography>
          <LinearProgress />
        </Box>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          disabled={loading}
          startIcon={<CancelIcon />}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!selectedFile || loading || !!validationError}
          startIcon={<UploadIcon />}
        >
          {loading ? 'Uploading...' : 'Upload SVG'}
        </Button>
      </Box>
    </Box>
  );
};

export default PatternUpload;
